import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaCard from "@/components/base/DramaCard";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import HeroSlider from "@/pages/home/components/HeroSlider";
import CategoryShortcuts from "@/pages/home/components/CategoryShortcuts";
import RankList from "@/pages/home/components/RankList";
import { allDramas, getDramaById } from "@/lib/drama";

export default function Home() {
  const featured = allDramas.filter((item) => item.featured);
  const heroSlides = [
    ...featured,
    ...allDramas.filter((item) => !item.featured).sort((a, b) => b.heat - a.heat),
  ].slice(0, 3);

  const latest = [...allDramas]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 10);

  const hotList = [...allDramas].sort((a, b) => b.heat - a.heat).slice(0, 8);
  const editorPick = getDramaById("saozi-de-zhiye");

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main>
        <HeroSlider slides={heroSlides} />

        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8">
          <div className="pt-10 md:pt-14">
            <CategoryShortcuts />
          </div>

          <section className="pt-12 md:pt-16" aria-labelledby="latest-title">
            <SectionHeading
              eyebrow="NEW EPISODES"
              title="最新更新剧集"
              description="按更新时间排序，追更不落下一集"
              action={
                <Link
                  to="/category?sort=update"
                  className="whitespace-nowrap text-xs text-primary-600 transition-colors hover:text-primary-800 md:text-sm"
                >
                  查看全部
                  <i className="ri-arrow-right-s-line ml-0.5" aria-hidden="true" />
                </Link>
              }
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {latest.map((drama, index) => (
                <Reveal key={drama.id} delay={index * 40}>
                  <DramaCard drama={drama} />
                </Reveal>
              ))}
            </div>
          </section>

          <section className="pt-12 md:pt-16" aria-labelledby="hot-title">
            <SectionHeading
              eyebrow="TRENDING NOW"
              title="本周热播榜"
              description="站内热度排序，看看大家都在追什么"
              action={
                <Link
                  to="/category?sort=heat"
                  className="whitespace-nowrap text-xs text-primary-600 transition-colors hover:text-primary-800 md:text-sm"
                >
                  完整榜单
                  <i className="ri-arrow-right-s-line ml-0.5" aria-hidden="true" />
                </Link>
              }
            />
            <RankList items={hotList} />
          </section>

          {editorPick && (
            <section className="pt-12 md:pt-16" aria-labelledby="editor-title">
              <SectionHeading eyebrow="EDITOR'S PICK" title="编辑精选" description="本周值得花时间看完的一部剧" />
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="flex flex-col md:flex-row">
                  <div className="relative h-[220px] w-full shrink-0 overflow-hidden md:h-auto md:w-[420px]">
                    <img
                      src={editorPick.banner}
                      alt={`${editorPick.title} 剧照`}
                      title={`${editorPick.title} 高清电视剧在线观看`}
                      className="h-full w-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-950/60 to-transparent md:bg-gradient-to-r" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col justify-center p-5 md:p-8">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] font-medium text-accent-800">
                        主推剧集
                      </span>
                      <span className="text-[11px] text-foreground-500">
                        {editorPick.year} · {editorPick.region} · {editorPick.status}
                      </span>
                    </div>
                    <h3 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                      {editorPick.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500">
                      主演：{editorPick.cast.join("、")} · 导演：{editorPick.director}
                    </p>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
                      {editorPick.synopsis}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {editorPick.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                      <Link
                        to={`/detail/${editorPick.id}`}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                      >
                        <i className="ri-play-fill" aria-hidden="true" />
                        查看剧集
                      </Link>
                      <Link
                        to="/category"
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                      >
                        更多同题材
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="pt-12 md:pt-16" aria-labelledby="about-brief-title">
            <div className="rounded-lg bg-primary-950 px-6 py-8 md:px-10 md:py-10">
              <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div className="max-w-2xl">
                  <h2 id="about-brief-title" className="font-heading text-xl font-bold text-background-50 md:text-2xl">
                    一个只为追剧而生的内容站
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-background-300">
                    我们整理题材标签、演职信息与更新状态，让找剧这件事变得简单。所有资料均以演示数据呈现，
                    不提供任何影视资源文件，播放功能仅作交互演示。
                  </p>
                </div>
                <div className="flex flex-wrap gap-6 md:gap-8">
                  <div>
                    <p className="font-heading text-2xl font-bold text-accent-400">{allDramas.length}</p>
                    <p className="mt-1 text-[11px] text-background-400">收录剧集</p>
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold text-accent-400">7</p>
                    <p className="mt-1 text-[11px] text-background-400">题材分类</p>
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold text-accent-400">8 分 +</p>
                    <p className="mt-1 text-[11px] text-background-400">口碑剧集</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex flex-col gap-3 border-t border-background-50/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-background-400">想看更多题材？去完整分类页按条件筛选。</p>
                <Link
                  to="/category"
                  className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
                >
                  进入分类页
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}